/* Responsibilities

This component should own:

Overall page layout
Fetching the waiver
Loading state
Header
Change Summary
Tabs
<Outlet /> for the current editor page
Passing the waiver to child pages via Outlet context

It should not contain any editing logic. 

Later your pages can simply do:
import { useOutletContext } from "react-router-dom";
const { waiver, setWaiver } = useOutletContext();
No refetching required.

//if status is publisehd, or archived, the editor link must never open, as these waivers are not editable. Redirect to archive screen, or show error message.

*/
import { useEffect, useState } from "react";

import {
    Outlet,
    useParams,
    Navigate,
    useLocation,
    useNavigate,
} from "react-router-dom";

import Sidebar from "../../components/Sidebar";
import { useBusinessAuthContext } from "../commoncomponents/useBusinessAuthContext";
import API_URL from "../../api/API";

import WaiverHeader from "./components/WaiverHeader";
import ChangeSummary from "./components/ChangeSummary";
import WaiverTabs from "./components/WaiverTabs";

import ConfirmModal from "../../components/ConfirmModal";

const WaiverEditorLayout = () => {

    const { waiverId } = useParams();
    const { business } = useBusinessAuthContext();

    const [loading, setLoading] = useState(true);
    const [waiver,setWaiver]=useState(null);

    const [templates,setTemplates]=useState(null);

    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

    const [saveHandler, setSaveHandler] = useState(null);  ////////NEW 3rd July 2026
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);  ////////NEW 3rd July 2026
    

    const location = useLocation();
    const navigate = useNavigate();

    //cannot acess locations tab for new version waivers
    useEffect(() => {
        if (!waiver) return;

        if (
            waiver.waiverStatus === "new_version" &&
            location.pathname.endsWith("/locations")
        ) {
            navigate(
                `/business/waivereditor/${waiver._id}/content`,
                { replace: true }
            );
        }
    }, [waiver, location.pathname, navigate]);

    const refreshWaiver = async () => {
        try {

            const res = await fetch(
                `${API_URL.BUSINESS_WAIVER_BY_ID}/${waiverId}`,
                {
                    headers: {
                        Authorization: `Bearer ${business.token}`,
                        "x-session-id": business.sessionId,
                        "x-frontend-url": window.location.pathname
                    }
                }
            );

            const data = await res.json();

            if (data.success) {
                setWaiver(data.waiver);
                setTemplates(data.templates);
            }
            else {
                alert(data.message);
            }
        }
        catch (err) {
            console.error(err);
            alert("Failed loading waiver.");
        }
        finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!business) return;
        refreshWaiver();
    }, [business, waiverId]);


    const refreshChangeSummary = async () => {
        const res = await fetch(
            `${API_URL.BUSINESS_WAIVER_BY_ID}/${waiverId}`,
            {
                headers: {
                    Authorization: `Bearer ${business.token}`,
                    "x-session-id": business.sessionId,
                    "x-frontend-url": window.location.pathname,
                },
            }
        );

        const data = await res.json();

        if (data.success) {
            setWaiver(prev => ({
                ...prev,
                changeSummary: data.waiver.changeSummary,
                minorWaiverAllowed: data.waiver.minorWaiverAllowed,
                returningSignerPolicy: data.waiver.returningSignerPolicy,
                locations: data.waiver.locations,
            }));
        }
    };

   //discard handle - called from WaiverHeader.js
    const handleDiscardDraft = async () => {
        try {
            let endpoint = "";
            let method = "";

            if (waiver.waiverStatus === "new_version") {
                endpoint = `${API_URL.BUSINESS_DISCARD_NEW_VERSION}/${waiver._id}`;
                method = "DELETE";
            }
            else if (waiver.waiverStatus === "draft") {
                endpoint = `${API_URL.BUSINESS_WAIVER_DISABLE}/${waiver._id}`;
                method = "PATCH";
            }
            else {
                return;
            }

            const res = await fetch(endpoint, {
                method,
                headers: {
                    Authorization: `Bearer ${business.token}`,
                    "x-session-id": business.sessionId,
                    "x-frontend-url": window.location.pathname,
                },
            });

            const data = await res.json();

            if (!data.success) {
                alert(data.message || "Failed to discard draft.");
                return;
            }

            navigate("/business/waivers");

        } catch (err) {
            console.error(err);
            alert("Server error");
        } finally {
            setShowDiscardConfirm(false);
        }
    };

    const handleReviewAndPublish = async () => {

        try {
            const res = await fetch(
                `${API_URL.BUSINESS_WAIVER_CHECK_ELIGIBILITY_CHANGES}/${waiverId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${business.token}`,
                        "x-session-id": business.sessionId,
                        "x-frontend-url": window.location.pathname,
                    },
                }
            );

            const data = await res.json();

            // ---------------------------------------------
            // Eligibility changes require confirmation
            // ---------------------------------------------
            if (
                !data.success &&
                data.code === "ELIGIBILITY_CHANGES_NOT_ACCEPTED"
            ) {

                navigate(
                    `/business/waivereditor/${waiverId}/eligibility`,
                    {
                        state: {
                            showEligibilityConfirm: true,
                        },
                    }
                );

                return;
            }

            // ---------------------------------------------
            // Other errors
            // ---------------------------------------------
            if (!data.success) {

                alert(
                    data.message ||
                    "Unable to continue to Review & Publish."
                );

                return;
            }

            // ---------------------------------------------
            // Mark Step 6 complete
            // ---------------------------------------------
            const stepRes = await fetch(
                `${API_URL.BUSINESS_WAIVER_COMPLETE_STEP_WAIVER_SECTIONS}/${waiverId}`,
                {
                    method: "PATCH",
                    headers: {
                        Authorization: `Bearer ${business.token}`,
                        "x-session-id": business.sessionId,
                        "x-frontend-url": window.location.pathname,
                        "Content-Type": "application/json"
                    }
                }
            );

            const stepData = await stepRes.json();

            if (!stepData.success) {

                alert(
                    stepData.message ||
                    "Unable to complete the waiver section."
                );

                return;
            }

            navigate(
                `/business/waivereditor/${waiver._id}/review`
            );

        } catch (err) {

            console.error(
                "Failed checking eligibility changes:",
                err
            );

            alert(
                "Unable to continue to Review & Publish."
            );
        }
    };

    ////////NEW 3rd July 2026
    const handleNavigate = async (to) => {
        if (hasUnsavedChanges && saveHandler) {
            const ok = await saveHandler();

            if (!ok) return;
        }

        navigate(to);
    };

    if (!business) {
        return <Navigate to="/business/login" replace />;
    }

    if (loading) {
        return (
            <div className="h-screen flex items-center justify-center">
                Loading...
            </div>
        );
    }

    if (!waiver) {
        return (
            <div className="h-screen flex items-center justify-center">
                Waiver not found.
            </div>
        );
    }

    return (

        <div className="h-screen flex bg-background">

            <Sidebar />

            <div className="flex-1 overflow-auto">

                <div className="max-w-7xl mx-auto px-8 py-8">

                    <WaiverHeader
                        waiver={waiver}
                        onDiscard={() => setShowDiscardConfirm(true)}
                        onPublish={handleReviewAndPublish}
                    />


                    <ChangeSummary summary={waiver.changeSummary} waiver={waiver} />
                                     

                    <WaiverTabs
                        waiver={waiver}
                        onNavigate={handleNavigate} ////////NEW 3rd July 2026
                    />

                    <div className="mt-6">

                       <Outlet
                            context={{
                                waiver,
                                templates,
                                setWaiver,
                                refreshChangeSummary,
                                setSaveHandler, ////////NEW 3rd July 2026
                                hasUnsavedChanges,
                                setHasUnsavedChanges,
                            }}
                        />

                    </div>

                </div>

            </div>

            <ConfirmModal
                open={showDiscardConfirm}
                title="Discard Draft"
                message={
                    waiver?.waiverStatus === "new_version"
                        ? "Are you sure you want to discard this draft version? Any unpublished changes will be permanently lost."
                        : "Are you sure you want to discard this draft waiver? Any unpublished changes will be permanently lost."
                }
                confirmText="Discard Draft"
                cancelText="Cancel"
                onConfirm={handleDiscardDraft}
                onCancel={() => setShowDiscardConfirm(false)}
            />

        </div>

    );

};

export default WaiverEditorLayout;